import {
  DiagnosticCenter,
  DiagnosticCenterListResponse,
  ApiDiagnosticCenterListResponse,
  DiagnosticCenterQueryParams,
} from './diagnostic-centers.dto';
import { logging, API_BASE_URL } from '@/config/env';

const customLog = (...args: unknown[]) => {
  if (logging) {
    console.log(...args);
  }
};

class DiagnosticCentersService {
  private baseUrl = API_BASE_URL;
  private logoutCallback: (() => void) | null = null;

  setLogoutCallback(callback: () => void) {
    this.logoutCallback = callback;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    customLog('=== API Request ===');
    customLog('URL:', url);
    customLog('Method:', options.method || 'GET');

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    customLog('Response status:', response.status);
    customLog('Response ok:', response.ok);

    if (!response.ok) {
      const errorText = await response.text();
      customLog('Error response:', errorText);

      try {
        const errorData = JSON.parse(errorText);
        if (
          errorData.status === false &&
          errorData.message === 'Invalid or expired admin session'
        ) {
          customLog('Session expired, triggering logout');
          if (this.logoutCallback) {
            this.logoutCallback();
          }
        }
      } catch (parseError) {
        // If parsing fails, continue with original error handling
      }

      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    customLog('Response data:', JSON.stringify(data, null, 2));

    if (
      data.status === false &&
      data.message === 'Invalid or expired admin session'
    ) {
      customLog('Session expired in response, triggering logout');
      if (this.logoutCallback) {
        this.logoutCallback();
      }
    }

    return data;
  }

  async getDiagnosticCenters(
    token: string,
    params: DiagnosticCenterQueryParams = {}
  ): Promise<DiagnosticCenterListResponse> {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.status !== undefined)
      queryParams.append('status', params.status.toString());
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const queryString = queryParams.toString();
    const endpoint = `/admin/diagnostic-centers${queryString ? `?${queryString}` : ''}`;

    const response = await this.request<ApiDiagnosticCenterListResponse>(
      endpoint,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    customLog('Processed diagnostic centers response:', response);

    return {
      data: response.data || [],
      meta: {
        total: response.total || 0,
        page: response.page || 1,
        lastPage: response.lastPage || 1,
        limit: params.limit || 10,
      },
    };
  }

  async getDiagnosticCenter(
    token: string,
    id: number
  ): Promise<DiagnosticCenter> {
    const response = await this.request<{
      status: boolean;
      message: string;
      data: DiagnosticCenter;
    }>(`/admin/diagnostic-centers/${id}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  }
}

export const diagnosticCentersService = new DiagnosticCentersService();
